import apiClient from './apiClient';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_BASE_URL = `${BASE_URL}/api`;

export const mediaApi = {
	// Folders
	folders: {
		getAll: async (orgId) => {
			if (!orgId) {
				throw new Error('Organization ID is required');
			}
			try {
				const response = await apiClient.get(
					`/api/folders/organization/${orgId}`
				);
				return response.data;
			} catch (error) {
				console.error('Get folders error:', error);
				throw new Error(
					error.response?.data?.message || 'Failed to fetch folders'
				);
			}
		},

		create: async (folderData) => {
			try {
				if (!folderData.name) {
					throw new Error('Folder name is required');
				}
				if (!folderData.orgId) {
					throw new Error('Organization ID is required');
				}
				if (!folderData.visibility) {
					throw new Error('Visibility is required');
				}

				const response = await apiClient.post(
					`/api/folders`,
					folderData
				);
				return response.data;
			} catch (error) {
				console.error('Create folder error:', {
					message: error.message,
					response: error.response?.data,
					status: error.response?.status,
				});
				throw new Error(
					error.response?.data?.message ||
						error.message ||
						'Failed to create folder'
				);
			}
		},

		update: async (folderId, folderData) => {
			try {
				const response = await apiClient.put(
					`/api/folders/${folderId}`,
					folderData
				);
				return response.data;
			} catch (error) {
				throw new Error(
					error.response?.data?.message || 'Failed to update folder'
				);
			}
		},

		delete: async (folderId) => {
			try {
				const response = await apiClient.delete(
					`/api/folders/${folderId}`
				);
				return response.data;
			} catch (error) {
				throw new Error(
					error.response?.data?.message || 'Failed to delete folder'
				);
			}
		},

		getOne: async (folderId) => {
			try {
				const response = await apiClient.get(
					`/api/folders/${folderId}`
				);
				return response.data;
			} catch (error) {
				throw new Error(
					error.response?.data?.message || 'Failed to fetch folder'
				);
			}
		},
	},

	// Media files
	files: {
		upload: async (formData) => {
			try {
				const response = await apiClient.post(`/api/media`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
				return response.data;
			} catch (error) {
				console.error('API Error:', {
					message:
						error.response?.data?.message ||
						'Failed to upload files',
					status: error.response?.status,
					data: error.response?.data,
				});
				throw new Error(
					error.response?.data?.message || 'Failed to upload files'
				);
			}
		},

		getAll: async (orgId, folderId = null) => {
			try {
				let url = `/api/media/organization/${orgId}`;
				if (folderId) {
					url += `?folderId=${folderId}`;
				}

				const response = await apiClient.get(url);
				return response.data;
			} catch (error) {
				console.error('Get files error:', error);
				throw new Error(
					error.response?.data?.message || 'Failed to fetch files'
				);
			}
		},

		delete: async (fileId) => {
			try {
				const response = await apiClient.delete(`/api/media/${fileId}`);
				return response.data;
			} catch (error) {
				throw new Error(
					error.response?.data?.message || 'Failed to delete file'
				);
			}
		},

		search: async (orgId, query) => {
			try {
				const response = await apiClient.get(
					`/api/media/search/${orgId}`,
					{
						params: { query },
					}
				);
				return response.data;
			} catch (error) {
				throw new Error(
					error.response?.data?.message ||
						'Failed to search media and folders'
				);
			}
		},

		batchCreate: async (mediaItems) => {
			try {
				const response = await apiClient.post(`/api/media/batch`, {
					mediaItems,
				});
				return response.data.media;
			} catch (error) {
				console.error('Batch create error:', error);
				throw new Error(
					error.response?.data?.message ||
						'Failed to create media items'
				);
			}
		},

		batchUpdate: async (mediaUpdates) => {
			try {
				const response = await apiClient.put(`/api/media/batch`, {
					mediaUpdates,
				});
				return response.data.media;
			} catch (error) {
				console.error('Batch update error:', error);
				throw new Error(
					error.response?.data?.message ||
						'Failed to update media items'
				);
			}
		},

		batchDelete: async (mediaIds) => {
			try {
				const response = await apiClient.delete(`/api/media/batch`, {
					data: { mediaIds },
				});
				return response.data;
			} catch (error) {
				console.error('Batch delete error:', error);
				throw new Error(
					error.response?.data?.message ||
						'Failed to delete media items'
				);
			}
		},
	},

	// Tags
	tags: {
		getAll: async (orgId) => {
			try {
				const response = await apiClient.get(
					`/api/tags/organization/${orgId}`
				);
				return response.data;
			} catch (error) {
				throw new Error(
					error.response?.data?.message || 'Failed to fetch tags'
				);
			}
		},

		create: async (tagData) => {
			try {
				const response = await apiClient.post(`/api/tags`, tagData);
				return response.data;
			} catch (error) {
				throw new Error(
					error.response?.data?.message || 'Failed to create tag'
				);
			}
		},

		update: async (tagId, tagData) => {
			try {
				const response = await apiClient.put(
					`/api/tags/${tagId}`,
					tagData
				);
				return response.data;
			} catch (error) {
				throw new Error(
					error.response?.data?.message || 'Failed to update tag'
				);
			}
		},

		delete: async (tagId) => {
			try {
				const response = await apiClient.delete(`/api/tags/${tagId}`);
				return response.data;
			} catch (error) {
				throw new Error(
					error.response?.data?.message || 'Failed to delete tag'
				);
			}
		},
	},
};
